import { useSyncExternalStore } from 'react'

// Module store for the Library ZIP selection, following
// `notifications/notification-store.ts`. The reason for the pattern rather than
// component state: `/assets` and `/assets/:id` are SIBLING routes, so opening one
// asset to check it fully unmounts the Library page — a selection held in that
// page's own `useState` would not survive an interaction the user makes all the
// time. The selection belongs to the Files experience, not to one mounted page.
//
// Selecting is a MODE, held here for the same reason the ids are: a reader who
// opens one of the files they picked comes back to the grid still picking.
//
// In memory only, and deliberately so: nothing here reads or writes any
// client-side storage, so a browser refresh starts empty with no reconstruction.

// Replaced (never mutated) on every change: `useSyncExternalStore` compares
// snapshots by identity, and a mutated Set would be the same reference and would
// not re-render.
let selected: ReadonlySet<string> = new Set()
let selecting = false
const listeners = new Set<() => void>()

function notify(): void {
	for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
	listeners.add(listener)
	return () => {
		listeners.delete(listener)
	}
}

function emit(next: ReadonlySet<string>): void {
	selected = next
	notify()
}

/**
 * Reads the current selection outside React.
 *
 * @returns {ReadonlySet<string>} The selected File ids.
 */
export function getSelection(): ReadonlySet<string> {
	return selected
}

/** Whether the grid is selecting: its cards are toggles rather than links. */
export function getSelecting(): boolean {
	return selecting
}

/**
 * Adds or removes one File.
 *
 * @param {string} fileId - The File to toggle.
 */
export function toggle(fileId: string): void {
	const next = new Set(selected)
	if (!next.delete(fileId)) next.add(fileId)
	emit(next)
}

/**
 * Selects or deselects a set of Files in one step, leaving every other selected
 * File untouched — which is what keeps `Select all` from ever reaching beyond the
 * ids its caller passed in.
 *
 * @param {readonly string[]} fileIds - The Files to change.
 * @param {boolean} select - True to add them all, false to remove them all.
 */
export function toggleMany(fileIds: readonly string[], select: boolean): void {
	if (fileIds.length === 0) return
	const next = new Set(selected)
	for (const fileId of fileIds) {
		if (select) next.add(fileId)
		else next.delete(fileId)
	}
	emit(next)
}

/** Enters the mode. The selection it starts from is whatever survived the last one. */
export function startSelecting(): void {
	if (selecting) return
	selecting = true
	notify()
}

/**
 * Leaves the mode and drops the selection — the two are one action. A selection
 * the grid no longer shows as selected is one the user cannot undo.
 *
 * A no-op when there is neither, so a repeated exit causes no render.
 */
export function exitSelecting(): void {
	if (!selecting && selected.size === 0) return
	selecting = false
	selected = new Set()
	notify()
}

/**
 * Subscribes a component to the selection.
 *
 * @returns {ReadonlySet<string>} The selected File ids, re-rendering on change.
 */
export function useLibrarySelection(): ReadonlySet<string> {
	return useSyncExternalStore(subscribe, getSelection, getSelection)
}

/**
 * Subscribes a component to the selection MODE.
 *
 * @returns {boolean} Whether the grid is selecting, re-rendering on change.
 */
export function useLibrarySelecting(): boolean {
	return useSyncExternalStore(subscribe, getSelecting, getSelecting)
}
